import React, { useMemo } from 'react'
import { useStyles } from './CustomDimension.styles'
import { FieldArray, FormikProps } from 'formik'

import type { Dimension } from '../../../../../types/flyout'
import { ButtonV2, TextInputV4 } from '@labrav/react-components'
import { get } from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons'
import { getRandom } from '../../../../../common/utils/helperFunctions'
import { Typography } from '@mui/material'
interface CustomDimensionProps {
  formik: FormikProps<Record<string, unknown>>
  heading?: string
  productId?: string
  subHeading?: string
  isFlexible?: boolean
  isResale?: boolean
  buttonName?: string
}

const findCustomIndex = (dimensions: Dimension[]) => {
  return dimensions.filter(dimension => dimension.type === 'custom').length
}

export const CustomDimension = ({
  formik,
  heading,
  productId,
  subHeading,
  isFlexible = false,
  isResale = false,
  buttonName = 'Add more custom dimensions',
}: CustomDimensionProps) => {
  const { classes } = useStyles({ isResale })
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } =
    formik
  const dimensions = (values.dimensions || []) as Dimension[]
  const standardDimensionsLength = dimensions?.filter(
    dimensionItem => dimensionItem.type === 'standard'
  ).length
  const customDimensionLength = dimensions?.filter(
    dimensionItem => dimensionItem.type === 'custom'
  ).length
  const standardCustomDimensionLength =
    standardDimensionsLength + customDimensionLength

  const emptyCustomDimension = {
    awsProductId: productId,
    type: 'custom',
    name: '',
    label: '',
    quantity: '',
    price: '',
    labelDescription: '',
    currency: 'USD',
    isAdditionalUsageAllowed: false,
  }
  const randomKey = useMemo(() => getRandom(), [dimensions.length])
  return (
    <div className={classes.root}>
      <div className={classes.heading}>{heading}</div>
      <div className={classes.value}>{subHeading}</div>
      <FieldArray name="dimensions">
        {({ push, remove }) => (
          <>
            {dimensions.map((dimension, index) =>
              dimension.type === 'custom' ? (
                <>
                  <div
                    className={classes.customDimensionWrapper}
                    data-testid="dimensions-row"
                  >
                    <div className={classes.innerWrapper}>
                      <div className={classes.outerWrapper}>
                        <div className={classes.heading}>
                          Custom dimension{' '}
                          {findCustomIndex(dimensions.slice(0, index)) + 1}
                        </div>
                        <div className={classes.trashIcon}>
                          {standardCustomDimensionLength > 1 && (
                            <FontAwesomeIcon
                              icon={faTrash}
                              style={{ width: '18px', height: '20px' }}
                              data-testid="trash-dimensions"
                              onClick={() => remove(index)}
                            />
                          )}
                        </div>
                      </div>
                      <div
                        className={classes.resourcesContainer}
                        key={`dimensions-${randomKey}-name`}
                      >
                        <div
                          className={classes.resouceField}
                          key={`dimensions-${randomKey}-label`}
                        >
                          <TextInputV4
                            keyName={`dimensions.${[index]}.label`}
                            title="Enter a display name"
                            type="string"
                            placeHolder="Enter a display name"
                            value={
                              dimension?.label
                                ? (dimension?.label as string)
                                : ''
                            }
                            isError={
                              !!(
                                get(
                                  touched,
                                  `dimensions.${[index]}.label`,
                                  false
                                ) &&
                                get(
                                  errors,
                                  `dimensions.${[index]}.label`,
                                  false
                                )
                              )
                            }
                            errorText={
                              (!!get(
                                touched,
                                `dimensions.${[index]}.label`,
                                false
                              ) &&
                                get(
                                  errors,
                                  `dimensions.${[index]}.label`,
                                  ''
                                )) ||
                              ''
                            }
                            onChange={e => {
                              handleChange(e)
                              setFieldValue(
                                `dimensions.${index}.name`,
                                e.target.value
                                  .replace(/[^a-zA-Z0-9]/g, '_')
                                  .slice(0, 36)
                              )
                            }}
                            isReadOnly={false}
                            isRequired={false}
                            showSpinner={false}
                            onBlur={handleBlur}
                          />
                        </div>
                        {isFlexible ? (
                          <div
                            className={classes.resouceField}
                            key={`dimensions-${randomKey}-quantity`}
                          >
                            <TextInputV4
                              keyName={`dimensions.${[index]}.quantity`}
                              title="Quantity"
                              type="number"
                              placeHolder="Quantity"
                              value={
                                dimension?.quantity
                                  ? (dimension?.quantity as number)
                                  : ''
                              }
                              isError={
                                !!(
                                  get(
                                    touched,
                                    `dimensions.${[index]}.quantity`,
                                    false
                                  ) &&
                                  get(
                                    errors,
                                    `dimensions.${[index]}.quantity`,
                                    false
                                  )
                                )
                              }
                              errorText={
                                (!!get(
                                  touched,
                                  `dimensions.${[index]}.quantity`,
                                  false
                                ) &&
                                  get(
                                    errors,
                                    `dimensions.${[index]}.quantity`,
                                    ''
                                  )) ||
                                ''
                              }
                              onChange={handleChange}
                              isReadOnly={false}
                              isRequired={false}
                              showSpinner={false}
                              onBlur={handleBlur}
                              keyToPrevent={['+', '-', 'e']}
                            />
                          </div>
                        ) : (
                          <div
                            className={classes.resouceField}
                            key={`dimensions-${randomKey}-price`}
                          >
                            <TextInputV4
                              keyName={`dimensions.${[index]}.price`}
                              title="Price"
                              type="number"
                              placeHolder="Price"
                              value={
                                dimension?.price
                                  ? (dimension?.price as number)
                                  : ''
                              }
                              isError={
                                !!(
                                  get(
                                    touched,
                                    `dimensions.${[index]}.price`,
                                    false
                                  ) &&
                                  get(
                                    errors,
                                    `dimensions.${[index]}.price`,
                                    false
                                  )
                                )
                              }
                              errorText={
                                (!!get(
                                  touched,
                                  `dimensions.${[index]}.price`,
                                  false
                                ) &&
                                  get(
                                    errors,
                                    `dimensions.${[index]}.price`,
                                    ''
                                  )) ||
                                ''
                              }
                              onChange={handleChange}
                              isReadOnly={false}
                              isRequired={false}
                              showSpinner={false}
                              onBlur={handleBlur}
                              keyToPrevent={['+', '-', 'e']}
                            />
                          </div>
                        )}
                      </div>
                      <div
                        className={classes.resouceFieldDown}
                        key={`dimensions-${randomKey}-labelDescription`}
                      >
                        <TextInputV4
                          keyName={`dimensions.${[index]}.labelDescription`}
                          title="Enter a brief description"
                          type="string"
                          placeHolder="Enter a brief description"
                          value={
                            dimension?.labelDescription
                              ? (dimension?.labelDescription as string)
                              : ''
                          }
                          isError={
                            !!(
                              get(
                                touched,
                                `dimensions.${[index]}.labelDescription`,
                                false
                              ) &&
                              get(
                                errors,
                                `dimensions.${[index]}.labelDescription`,
                                false
                              )
                            )
                          }
                          errorText={
                            (!!get(
                              touched,
                              `dimensions.${[index]}.labelDescription`,
                              false
                            ) &&
                              get(
                                errors,
                                `dimensions.${[index]}.labelDescription`,
                                ''
                              )) ||
                            ''
                          }
                          onChange={handleChange}
                          isReadOnly={false}
                          isRequired={false}
                          showSpinner={false}
                          onBlur={handleBlur}
                          keyToPrevent={['+', '-', 'e']}
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <></>
              )
            )}

            {!isResale && (
              <div
                className={classes.endHeading}
                onClick={() => {
                  push(emptyCustomDimension)
                }}
                data-testid={'add-a-dimensions-button'}
              >
                Add more custom dimensions
              </div>
            )}
            {standardCustomDimensionLength <= 23 && isResale ? (
              <div>
                <ButtonV2
                  className={classes.widthButton}
                  styleType="link"
                  onClick={() => {
                    push(emptyCustomDimension)
                  }}
                  data-testid="add-a-dimensions-button"
                  smallVariant
                  icon={
                    <FontAwesomeIcon
                      icon={faPlus}
                      style={{ width: '14px', height: '16px' }}
                    />
                  }
                >
                  {buttonName}
                </ButtonV2>
                <Typography className={classes.buttonSubText}>
                  You can add upto {24 - standardCustomDimensionLength} custom
                  dimensions.
                </Typography>
              </div>
            ) : (
              <></>
            )}
          </>
        )}
      </FieldArray>
    </div>
  )
}
